import { useState, useRef, useEffect, useCallback } from 'react';
import { MediaRecorderService } from '../audio/media-recorder-service';
import { validateAudioBlob, AudioValidationResult } from '../audio/audio-validator';
import { uploadVoiceMemo, UploadVoiceMemoResult } from '../audio/storage-upload-service';
import { MimeTypeConfig } from '../audio/mime-negotiator';

export interface UseAudioRecorderReturn {
  isRecording: boolean;
  isPaused: boolean;
  recordingDuration: number;
  audioBlob: Blob | null;
  audioUrl: string | null;
  validationResult: AudioValidationResult | null;
  error: string | null;
  isUploading: boolean;
  uploadProgress: number;
  mimeConfig: MimeTypeConfig | null;
  startRecording: () => Promise<void>;
  pauseRecording: () => void;
  resumeRecording: () => void;
  stopRecording: () => Promise<Blob | null>;
  cancelRecording: () => void;
  handleFileSelect: (file: File) => Promise<AudioValidationResult>;
  clearAudio: () => void;
  uploadAudio: (params: {
    coachId: string;
    coachName: string;
    patientId: string;
    domainId: string;
    memoTitle: string;
  }) => Promise<UploadVoiceMemoResult>;
}

export function useAudioRecorder(): UseAudioRecorderReturn {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [validationResult, setValidationResult] = useState<AudioValidationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [mimeConfig, setMimeConfig] = useState<MimeTypeConfig | null>(null);

  const recorderRef = useRef<MediaRecorderService | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Clean up object URLs and timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (audioUrl) {
        try {
          URL.revokeObjectURL(audioUrl);
        } catch {
          // Ignore
        }
      }
      if (recorderRef.current) {
        recorderRef.current.cancel();
      }
    };
  }, [audioUrl]);

  const clearAudio = useCallback(() => {
    if (audioUrl) {
      try {
        URL.revokeObjectURL(audioUrl);
      } catch {
        // Ignore
      }
    }
    setAudioBlob(null);
    setAudioUrl(null);
    setValidationResult(null);
    setError(null);
    setRecordingDuration(0);
  }, [audioUrl]);

  const startRecording = useCallback(async () => {
    try {
      clearAudio();
      setError(null);
      if (!recorderRef.current) {
        recorderRef.current = new MediaRecorderService();
      }
      const config = await recorderRef.current.startRecording();
      setMimeConfig(config);
      setIsRecording(true);
      setIsPaused(false);
      setRecordingDuration(0);

      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setRecordingDuration((prev) => prev + 1);
      }, 1000);
    } catch (err: any) {
      setIsRecording(false);
      setIsPaused(false);
      setError(err?.message || 'Failed to start microphone recording.');
      throw err;
    }
  }, [clearAudio]);

  const pauseRecording = useCallback(() => {
    if (recorderRef.current && isRecording && !isPaused) {
      recorderRef.current.pause();
      setIsPaused(true);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  }, [isRecording, isPaused]);

  const resumeRecording = useCallback(() => {
    if (recorderRef.current && isRecording && isPaused) {
      recorderRef.current.resume();
      setIsPaused(false);
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setRecordingDuration((prev) => prev + 1);
      }, 1000);
    }
  }, [isRecording, isPaused]);

  const stopRecording = useCallback(async (): Promise<Blob | null> => {
    if (!recorderRef.current || !isRecording) return null;

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    try {
      const session = await recorderRef.current.stopRecording();
      setIsRecording(false);
      setIsPaused(false);

      setAudioBlob(session.blob);
      setValidationResult(session.validation);

      if (typeof window !== 'undefined' && typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function') {
        const url = URL.createObjectURL(session.blob);
        setAudioUrl(url);
      }

      if (!session.validation.isValid) {
        setError(session.validation.error || 'Recorded audio failed validation check.');
      } else {
        setError(null);
      }

      return session.blob;
    } catch (err: any) {
      setIsRecording(false);
      setIsPaused(false);
      setError(err?.message || 'Error occurred while finalizing recording.');
      throw err;
    }
  }, [isRecording]);

  const cancelRecording = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (recorderRef.current) {
      recorderRef.current.cancel();
    }
    setIsRecording(false);
    setIsPaused(false);
    clearAudio();
  }, [clearAudio]);

  const handleFileSelect = useCallback(async (file: File): Promise<AudioValidationResult> => {
    clearAudio();
    setError(null);

    const validation = await validateAudioBlob(file);
    setAudioBlob(file);
    setValidationResult(validation);

    if (typeof window !== 'undefined' && typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function') {
      const url = URL.createObjectURL(file);
      setAudioUrl(url);
    }

    if (!validation.isValid) {
      setError(validation.error || 'Attached audio file failed validation check.');
    } else {
      setError(null);
    }

    return validation;
  }, [clearAudio]);

  const uploadAudio = useCallback(async (params: {
    coachId: string;
    coachName: string;
    patientId: string;
    domainId: string;
    memoTitle: string;
  }): Promise<UploadVoiceMemoResult> => {
    if (!audioBlob) {
      throw new Error('No audio recording or file ready for upload.');
    }

    if (validationResult && !validationResult.isValid) {
      throw new Error(`Cannot upload invalid audio: ${validationResult.error}`);
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const ext =
        validationResult?.detectedFormat !== 'unknown' && validationResult?.detectedFormat
          ? validationResult.detectedFormat
          : mimeConfig?.extension || 'm4a';

      const duration = validationResult?.durationSeconds || recordingDuration || 1.0;

      const result = await uploadVoiceMemo({
        audioBlob,
        coachId: params.coachId,
        coachName: params.coachName,
        patientId: params.patientId,
        domainId: params.domainId,
        memoTitle: params.memoTitle,
        durationSeconds: duration,
        extension: ext,
        onProgress: (p) => setUploadProgress(p),
      });

      setIsUploading(false);
      setUploadProgress(100);
      return result;
    } catch (err: any) {
      setIsUploading(false);
      setError(err?.message || 'Failed to upload audio to cloud storage.');
      throw err;
    }
  }, [audioBlob, validationResult, mimeConfig, recordingDuration]);

  return {
    isRecording,
    isPaused,
    recordingDuration,
    audioBlob,
    audioUrl,
    validationResult,
    error,
    isUploading,
    uploadProgress,
    mimeConfig,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    cancelRecording,
    handleFileSelect,
    clearAudio,
    uploadAudio,
  };
}
